import lodash from 'lodash';
import { ProGallery } from 'pro-gallery';
import { migrateOptions } from 'pro-gallery-lib';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

import type { GalleryData_Item } from './../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { TFunction } from './../../../ricos-types/index.js';
import { FocusManager } from './../../../_shared/focus-manager/focus-manager.js';
import { convertToGalleryItem } from './../../../_shared/media/convert-to-gallery-item.js';
import { fullscreenResizeMediaUrl } from './../../../_shared/media/fullscreen-resize-media-url.js';

import {
  FULLSCREEN_CLOSE_BUTTON,
  FULLSCREEN_IMAGE_ITEM,
  FULLSCREEN_ROOT,
  FULLSCREEN_TOGGLE_BUTTON,
} from './data-hooks.js';
import styles from './fullscreen-scss.js';

export type FullscreenModalProps = {
  container?: HTMLElement | (() => HTMLElement | null);
} & InnerFullscreenProps;

const FullscreenModal: React.FC<FullscreenModalProps> = ({
  container = document.body,
  ...props
}) => {
  const target = lodash.isFunction(container) ? container() : container;

  const fullscreen = <InnerFullscreen {...props} />;

  return target ? ReactDOM.createPortal(fullscreen, target) : fullscreen;
};

export type FullscreenModalItem = GalleryData_Item & { disableDownload?: boolean };

type InnerFullscreenProps = {
  images: FullscreenModalItem[];
  isMobile: boolean;
  index: number;
  backgroundColor?: string;
  foregroundColor?: string;
  onClose: () => void;
  jsdomMode?: boolean;
  t: TFunction;
};

const InnerFullscreen: React.FC<InnerFullscreenProps> = props => {
  const { backgroundColor = 'white', foregroundColor = '#2F2E2E', isMobile, t } = props;

  const [isInFullscreen, setIsInFullscreen] = React.useState(false);
  const [closeButtonRef, setCloseButtonRef] = React.useState<HTMLButtonElement | null>(null);
  const [size, setSize] = React.useState({ width: window.innerWidth, height: window.innerHeight });
  const items = props.images.map((item, index) => ({
    ...convertToGalleryItem({ item, index }),
    disableDownload: item.disableDownload,
  }));
  const containerRef = React.useRef<HTMLDivElement>(null);
  const currentIdx = React.useRef<number>(props.index || 0);

  useEffect(() => {
    if (closeButtonRef && !isMobile) {
      setTimeout(() => {
        closeButtonRef.focus();
      }, 0);
    }
  }, [closeButtonRef]);

  const toggleFullscreenMode = () => {
    const element = document.body;

    if (document.fullscreenEnabled) {
      if (isInFullscreen) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      } else if (element.requestFullscreen) {
        element.requestFullscreen();
      }
    }
  };

  const onClose = () => {
    if (isInFullscreen) {
      toggleFullscreenMode();
    }
    props.onClose();
  };

  React.useEffect(() => {
    pauseChildNodesVideos(document);

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const onWindowResize = () => setSize(getDimensions(containerRef.current));

    const onFullscreenChange = () => setIsInFullscreen(!!document.fullscreenElement);

    document.addEventListener('keydown', onEsc);
    window.addEventListener('resize', onWindowResize);

    if (document.fullscreenEnabled) {
      document.addEventListener('fullscreenchange', onFullscreenChange);
    }

    setSize(getDimensions(containerRef.current));

    return () => {
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('resize', onWindowResize);

      if (document.fullscreenEnabled) {
        document.removeEventListener('fullscreenchange', onFullscreenChange);
      }
    };
  }, []);

  const { width, height } = size;
  const isHorizontalView = width > height;
  const { arrowsPosition, slideshowInfoSize } = getStyleParams({
    isHorizontalView,
    isMobile,
    isInFullscreen,
  });

  const infoElement = (itemProps: { title?: string }) => {
    return itemProps.title ? (
      <div className={styles.info_container}>
        <div title={itemProps.title} style={{ color: foregroundColor }} className={styles.title}>
          {itemProps.title}
        </div>
      </div>
    ) : null;
  };

  const handleGalleryEvents = (name: string, data: Record<string, any>) => {
    if (name === 'CURRENT_ITEM_CHANGED') {
      const previousItem = items[currentIdx.current];
      if (previousItem?.metaData?.type === 'video') {
        pauseChildNodesVideos(
          document.querySelector(`[data-id='${previousItem.itemId}']`) as Element
        );
      }
      currentIdx.current = (data as { idx: number }).idx;
    }
  };

  const closeButton = (
    <button
      ref={setCloseButtonRef}
      type="button"
      className={styles.close}
      onClick={onClose}
      onKeyDown={applyOnEnterKeyPress(onClose)}
      aria-label={t('Fullscreen_Close_Button_AriaLabel')}
      data-hook={FULLSCREEN_CLOSE_BUTTON}
    >
      <CloseIcon backgroundColor={backgroundColor} foregroundColor={foregroundColor} />
    </button>
  );

  return (
    <FocusManager jsdomMode={props.jsdomMode}>
      <div
        style={{ background: backgroundColor }}
        dir="ltr"
        data-hook={FULLSCREEN_ROOT}
        className={
          isInFullscreen || (isMobile && isHorizontalView)
            ? styles.fullscreen_mode
            : styles.expand_mode
        }
        ref={containerRef}
        onContextMenu={(e: React.MouseEvent) =>
          items[currentIdx.current].disableDownload && e.preventDefault()
        }
        role="none"
      >
        <div aria-label={`Gallery with ${items.length} items`} style={{ height: '100%' }}>
          {!isMobile && (
            <FullscreenToggleButton
              isInFullscreen={isInFullscreen}
              backgroundColor={backgroundColor}
              foregroundColor={foregroundColor}
              onToggleFullscreen={toggleFullscreenMode}
              t={t}
            />
          )}
          {closeButton}
          <ProGallery
            id="ricos-fullscreen"
            scrollingElement={window}
            items={items}
            activeIndex={currentIdx.current}
            eventsListener={handleGalleryEvents}
            createMediaUrl={fullscreenResizeMediaUrl}
            container={{ width, height: isMobile ? height : height - 80 }}
            options={{
              ...migrateOptions({
                ...fixedOptions,
                showArrows: !isMobile,
                arrowsPosition,
                slideshowInfoSize,
                behaviourParams_item_overlay_hoveringBehaviour: 'NEVER_SHOW',
              }),
              layoutParams_info_height: 60,
            }}
            customComponents={{
              customImageRenderer,
              customInfoRenderer: !isInFullscreen ? infoElement : undefined,
            }}
            totalItemsCount={items.length}
            settings={{ isAccessible: true }}
            disableItemFocus={items.length === 1}
          />
        </div>
      </div>
    </FocusManager>
  );
};

const getDimensions = (element: HTMLElement | null) => {
  const container = element?.getBoundingClientRect?.();
  const width = container?.width || 0;
  const height = container?.height || 0;
  return { width, height };
};

const customImageRenderer = (itemProps: { [key: string]: any }) => {
  const {
    style,
    className,
    alt,
    tabIndex: _tabIndex,
    ...rest
  } = itemProps as {
    style: React.CSSProperties;
    className: string;
    alt: string;
    tabIndex?: number;
  };

  return (
    <div className={className} style={style} data-hook={FULLSCREEN_IMAGE_ITEM}>
      <img className={className} alt={alt} style={style} {...rest} />
    </div>
  );
};

const applyOnEnterKeyPress = (callback: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === 'Enter') {
    callback();
  }
};

const FullscreenToggleButton = ({
  isInFullscreen,
  backgroundColor,
  foregroundColor,
  onToggleFullscreen,
  t,
}: {
  isInFullscreen: boolean;
  backgroundColor?: string;
  foregroundColor?: string;
  onToggleFullscreen: () => void;
  t: TFunction;
}) => {
  const Icon = isInFullscreen ? ShrinkIcon : ExpandIcon;
  return (
    <button
      type="button"
      className={styles.expand_button}
      onClick={onToggleFullscreen}
      onKeyDown={applyOnEnterKeyPress(onToggleFullscreen)}
      aria-label={
        isInFullscreen
          ? t('Fullscreen_Shrink_Button_AriaLabel')
          : t('Fullscreen_Expand_Button_AriaLabel')
      }
      data-hook={FULLSCREEN_TOGGLE_BUTTON}
    >
      <Icon backgroundColor={backgroundColor} foregroundColor={foregroundColor} />
    </button>
  );
};

const pauseChildNodesVideos = (element: Document | Element) =>
  Array.from(element.getElementsByTagName('video'))?.forEach(video => video.pause());

const getStyleParams = ({
  isHorizontalView,
  isMobile,
  isInFullscreen,
}: {
  isHorizontalView: boolean;
  isMobile: boolean;
  isInFullscreen: boolean;
}) => {
  let arrowsPosition = 0;
  let slideshowInfoSize = 0;
  if (isMobile) {
    slideshowInfoSize = isHorizontalView ? 0 : 80;
  } else if (!isInFullscreen) {
    arrowsPosition = 1;
    slideshowInfoSize = 142;
  }
  return { arrowsPosition, slideshowInfoSize };
};

const fixedOptions = {
  galleryLayout: 5,
  cubeType: 'fit',
  scrollSnap: true,
  videoPlay: 'CLICK',
  videoLoop: false,
  videoSound: true,
  hidePlay: false,
  showVideoControls: true,
  showVideoPlayButton: true,
  allowSocial: false,
  loveButton: false,
  allowTitle: true,
  defaultShowInfoExpand: 1,
  floatingImages: 0,
  allowContextMenu: true,
};

const CloseIcon: React.FC<{ backgroundColor?: string; foregroundColor?: string }> = props => (
  <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">
    <g fill="none" fillRule="evenodd">
      <path fill={props.backgroundColor} d="M0 0H60V60H0z" />
      <path
        fill={props.foregroundColor}
        d="M42.188 17l.812.813L30.812 30 43 42.188l-.813.812L30 30.812 17.812 43 17 42.187 29.187 30 17 17.812l.813-.812L30 29.187 42.188 17z"
      />
    </g>
  </svg>
);

const ExpandIcon: React.FC<{ backgroundColor?: string; foregroundColor?: string }> = props => (
  <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">
    <g fill="none" fillRule="evenodd">
      <path fill={props.backgroundColor} d="M0 0H60V60H0z" />
      <g fill={props.foregroundColor}>
        <path
          d="M4.333 15.167H5.413V27.084H4.333z"
          transform="translate(17 17) rotate(45 4.873 21.125)"
        />
        <path d="M26 8h-1V1h-7V0h8v8z" transform="translate(17 17)" />
        <path
          d="M20.583 -1.083H21.665999999999997V10.834H20.583z"
          transform="translate(17 17) rotate(45 21.125 4.875)"
        />
        <path d="M0 26v-8h1v7h7v1H0z" transform="translate(17 17)" />
      </g>
    </g>
  </svg>
);

const ShrinkIcon: React.FC<{ backgroundColor?: string; foregroundColor?: string }> = props => (
  <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">
    <g fill="none" fillRule="evenodd">
      <path fill={props.backgroundColor} d="M0 0H60V60H0z" />
      <g fill={props.foregroundColor}>
        <path
          d="M4.055 16.447H5.135V28.363999999999997H4.055z"
          transform="translate(17 16) rotate(-135 4.595 22.405)"
        />
        <g>
          <path d="M10 8.083H9v-7H2v-1h8v8z" transform="translate(17 16) rotate(-180 13 5.458)" />
          <path
            d="M4.583 -1H5.666V10.917H4.583z"
            transform="translate(17 16) rotate(-180 13 5.458) rotate(45 5.125 4.958)"
          />
        </g>
        <path d="M9.468 17.53v8h-1v-7h-7v-1h8z" transform="translate(17 16)" />
      </g>
    </g>
  </svg>
);

// Lazy entry point
export default FullscreenModal;
